import { useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { bdErrorMessage, errorCode, isForbidden, isRetryable } from '../bdErrors';
import { EXCLUDED_REASON_TEXT, NO_CALCULATION_NOTE } from '../copy';
import { formatDate, formatMoney, todayIso } from '../dates';
import { employmentStatusLabels } from '../../people/types';
import { isActiveEmployee } from '../employeeDirectory';
import { newIdempotencyKey } from '../idempotency';
import { deductionsGateway } from '../index';
import { useEmployeeDirectory } from '../useEmployeeDirectory';
import {
  BdErrorCode, configurationStatusLabels, deductionCalculationTypeLabels,
  type BulkAssignmentPreview, type BulkAssignmentRequest, type BulkAssignmentResult, type BulkScope, type DeductionDefinition,
} from '../types';
import { SelectInput, TextInput } from './fields';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type ScopeChoice = 'single' | 'group' | 'all';
const MAX_SELECTED = 500;

export interface BulkAssignDialogProps {
  /** The deduction to assign. Omitted: the dialog asks which one, from `definitions`. */
  definition?: DeductionDefinition | null;
  definitions?: DeductionDefinition[];
  onClose: () => void;
  onDone: (result: BulkAssignmentResult, definition: DeductionDefinition) => void;
  onForbidden: () => void;
}

type Step = { kind: 'form' } | { kind: 'preview'; preview: BulkAssignmentPreview; request: BulkAssignmentRequest } | { kind: 'done'; result: BulkAssignmentResult };

/**
 * Assign a deduction to one employee, a selected group, or all eligible employees, in three explicit steps: choose, preview, confirm. The preview changes
 * nothing and shows the affected count, who is excluded and why, the calculation method, the effective date, the per-payroll estimate, the tax-treatment
 * status, the limits, conflicts and errors. Confirming is a separate step: it carries ONE idempotency key per confirmation attempt, reused if the same
 * attempt is retried, so a retry can never assign twice; a replay is reported as such. A preview with canConfirm = false cannot be confirmed, and says why.
 * Phase 1 payroll honors only fixed amounts, so percentage and other calculation types cannot be assigned yet.
 */
export function BulkAssignDialog({ definition: fixed, definitions = [], onClose, onDone, onForbidden }: BulkAssignDialogProps) {
  const { state: directory } = useEmployeeDirectory();
  const [definitionId, setDefinitionId] = useState(fixed?.id ?? '');
  const [scope, setScope] = useState<ScopeChoice>('single');
  const [singleId, setSingleId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [effective, setEffective] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [employer, setEmployer] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<Step>({ kind: 'form' });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [stale, setStale] = useState(false);
  // One key per confirmation attempt: created on the first Confirm, reused by every retry of that attempt, discarded when the request changes or it succeeds.
  const attemptKey = useRef<string | null>(null);

  const chosen = fixed ?? definitions.find((d) => d.id === definitionId) ?? null;
  const employees = useMemo(() => (directory.kind === 'ready' ? directory.employees : []), [directory]);
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return employees.filter((e) => !q || e.name.toLowerCase().includes(q) || e.employeeNumber.toLowerCase().includes(q));
  }, [employees, filter]);
  const nameOf = (id: string) => employees.find((e) => e.id === id)?.name ?? 'Unknown employee';

  function toggle(id: string) {
    setSelected((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  function parseOptional(raw: string, field: string, problems: Record<string, string>): number | null {
    const t = raw.trim();
    if (t === '') return null;
    if (!/^\d*\.?\d+$/.test(t)) { problems[field] = 'Enter a number, for example 25.50.'; return null; }
    return Number(t);
  }

  async function runPreview() {
    if (busy) return;
    const problems: Record<string, string> = {};
    if (!chosen) problems.definition = 'Choose a deduction.';
    if (!effective) problems.effective = 'Choose an effective date.';
    if (scope === 'single' && !singleId) problems.employees = 'Choose an employee.';
    if (scope === 'group' && selected.size === 0) problems.employees = 'Choose at least one employee.';
    if (scope === 'group' && selected.size > MAX_SELECTED) problems.employees = `Choose at most ${MAX_SELECTED} employees, or use all eligible employees.`;
    const amountOverride = parseOptional(amount, 'amount', problems);
    const companyContributionOverride = parseOptional(employer, 'employer', problems);
    setErrors(problems);
    setFormError(null);
    if (Object.keys(problems).length > 0) return;
    const request: BulkAssignmentRequest = {
      deductionCodeId: chosen!.id,
      scope: (scope === 'all' ? 'AllEligible' : 'Employees') as BulkScope,
      ...(scope === 'single' ? { employeeIds: [singleId] } : scope === 'group' ? { employeeIds: [...selected] } : {}),
      effectiveStartDate: effective,
      amountOverride,
      companyContributionOverride,
    };
    setBusy(true);
    try {
      const preview = await deductionsGateway.previewBulkAssignment(request);
      attemptKey.current = null;
      setStale(false);
      setStep({ kind: 'preview', preview, request });
    } catch (err) {
      if (isForbidden(err)) onForbidden();
      setFormError(bdErrorMessage(err, 'Could not preview this assignment.', 'assignment'));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (step.kind !== 'preview' || !chosen) return;
    // The first attempt makes the key; a retry of the SAME attempt (after a network or server failure) reuses it.
    attemptKey.current ??= newIdempotencyKey();
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const result = await deductionsGateway.confirmBulkAssignment({ ...step.request, idempotencyKey: attemptKey.current, confirm: true });
      attemptKey.current = null;
      setConfirming(false);
      setStep({ kind: 'done', result });
      onDone(result, chosen);
    } catch (err) {
      if (isForbidden(err)) onForbidden();
      const code = errorCode(err);
      if (code === BdErrorCode.IdempotencyKeyReused) {
        // That key now belongs to a different request: start a new attempt from a fresh preview.
        attemptKey.current = null;
        setConfirming(false);
        setStale(true);
        setFormError(bdErrorMessage(err, 'Could not confirm this assignment.', 'assignment'));
      } else if (isRetryable(err)) {
        setConfirmError(`${bdErrorMessage(err, 'Could not confirm this assignment.', 'assignment')} Nothing was confirmed. Trying again uses the same request key, so it cannot assign twice.`);
      } else {
        // A refusal (the definition changed, a 409): the key is spent on a request that did nothing, and the preview is out of date.
        attemptKey.current = null;
        setConfirming(false);
        setStale(true);
        setFormError(bdErrorMessage(err, 'Could not confirm this assignment.', 'assignment'));
      }
    } finally {
      setConfirmBusy(false);
    }
  }

  const definitionOptions = [
    { value: '', label: 'Choose a deduction' },
    ...definitions.map((d) => ({
      value: d.id,
      label: `${d.name}${!d.isActive ? ' (inactive)' : ''}${d.configurationStatus !== null && d.configurationStatus !== 'Complete' ? ' (needs review)' : ''}`,
    })),
  ];
  const employeeOptions = [
    { value: '', label: directory.kind === 'loading' ? 'Loading employees…' : 'Choose an employee' },
    ...employees.filter(isActiveEmployee).map((e) => ({ value: e.id, label: `${e.name} (${e.employeeNumber})` })),
  ];

  const footer = step.kind === 'done' ? (
    <button type="button" className="jr-btn jr-btn--primary" data-autofocus onClick={onClose}>Close</button>
  ) : step.kind === 'preview' ? (
    <>
      <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={confirmBusy}>Cancel</button>
      <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { attemptKey.current = null; setStale(false); setStep({ kind: 'form' }); }}>Change the choices</button>
      <button type="button" className="jr-btn jr-btn--primary" disabled={!step.preview.canConfirm || stale} onClick={() => { setConfirmError(null); setConfirming(true); }}>Confirm assignment</button>
    </>
  ) : (
    <>
      <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="button" className="jr-btn jr-btn--primary" disabled={busy} onClick={() => void runPreview()}>{busy ? 'Working…' : 'Preview'}</button>
    </>
  );

  return (
    <ModalDialog title={chosen ? `Assign ${chosen.name}` : 'Assign a deduction'} size="lg" busy={busy || confirmBusy} onClose={onClose} footer={footer}>
      {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}

      {step.kind === 'form' && (
        <div className="bd-form">
          <p className="bd-help">Choose who gets the deduction, then preview it. The preview changes nothing: you confirm in a separate step.</p>
          {fixed ? (
            <p className="bd-notice"><strong>{fixed.name}</strong>{fixed.calculationType ? `: ${deductionCalculationTypeLabels[fixed.calculationType]}` : ''}</p>
          ) : (
            <SelectInput id="bd-bulk-definition" label="Deduction" required value={definitionId} onChange={setDefinitionId} options={definitionOptions} error={errors.definition} />
          )}
          <fieldset className="bd-fieldset">
            <legend>Who gets it</legend>
            <label className="bd-check" htmlFor="bd-bulk-scope-single"><input id="bd-bulk-scope-single" type="radio" name="bd-bulk-scope" checked={scope === 'single'} onChange={() => setScope('single')} />One employee</label>
            <label className="bd-check" htmlFor="bd-bulk-scope-group"><input id="bd-bulk-scope-group" type="radio" name="bd-bulk-scope" checked={scope === 'group'} onChange={() => setScope('group')} />A selected group</label>
            <label className="bd-check" htmlFor="bd-bulk-scope-all"><input id="bd-bulk-scope-all" type="radio" name="bd-bulk-scope" checked={scope === 'all'} onChange={() => setScope('all')} />All eligible employees</label>
            {scope === 'all' && <p className="bd-help">Phase 1 eligibility means active employees who were hired on or before the effective date. The server works out the list.</p>}
            {scope === 'single' && <SelectInput id="bd-bulk-single" label="Employee" required value={singleId} onChange={setSingleId} options={employeeOptions} error={errors.employees} />}
            {scope === 'group' && (
              <>
                <TextInput id="bd-bulk-filter" label="Find employees" type="search" value={filter} onChange={setFilter} hint={`${selected.size} chosen. Choose up to ${MAX_SELECTED}.`} />
                <div className="bd-actions-row">
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setSelected((s) => new Set([...s, ...shown.map((e) => e.id)]))}>Choose all shown</button>
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setSelected(new Set())}>Clear choices</button>
                </div>
                <div className="bd-pick" role="group" aria-label="Employees">
                  {shown.length === 0 && <p className="muted">{directory.kind === 'loading' ? 'Loading employees…' : 'No employees match.'}</p>}
                  {shown.map((e) => (
                    <label key={e.id} htmlFor={`bd-bulk-emp-${e.id}`}>
                      <input id={`bd-bulk-emp-${e.id}`} type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                      {e.name} ({e.employeeNumber}){!isActiveEmployee(e) ? ` - ${employmentStatusLabels[e.status] ?? 'Not active'}` : ''}
                    </label>
                  ))}
                </div>
                {errors.employees && <p className="jr-error" role="alert">{errors.employees}</p>}
              </>
            )}
          </fieldset>
          <div className="jr-grid">
            <TextInput id="bd-bulk-effective" label="Effective date" type="date" required value={effective} onChange={setEffective} error={errors.effective} help="The first day the deduction applies. Employees hired after this date are left out." />
            <TextInput id="bd-bulk-amount" label="Amount for each employee (optional)" value={amount} onChange={setAmount} error={errors.amount} inputMode="decimal" hint={chosen?.defaultAmount != null ? `Leave empty to use the deduction's amount, ${formatMoney(chosen.defaultAmount)}.` : 'Leave empty to use the deduction\'s own amount.'} />
            <TextInput id="bd-bulk-employer" label="Employer contribution (optional)" value={employer} onChange={setEmployer} error={errors.employer} inputMode="decimal" />
          </div>
        </div>
      )}

      {step.kind === 'preview' && (
        <div className="bd-form" data-testid="bulk-preview">
          <h3 style={{ margin: 0 }}>Preview: nothing has changed yet</h3>
          {stale && <p className="bd-notice bd-notice--warn" role="status">The deduction changed or the request was refused. Choose again and preview again before confirming.</p>}
          <p className="bd-big" role="status">{step.preview.affectedCount} {step.preview.affectedCount === 1 ? 'employee' : 'employees'} would be affected</p>
          {!step.preview.canConfirm && (
            <div className="bd-errors" role="alert" data-testid="cannot-confirm">
              <p style={{ margin: '0 0 4px' }}><strong>This assignment cannot be confirmed yet.</strong></p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {step.preview.errors.length === 0 && <li>The server did not say why. Choose again and preview again.</li>}
                {step.preview.errors.map((e) => <li key={`${e.code}-${e.message}`}>{e.message}</li>)}
              </ul>
            </div>
          )}
          <dl className="bd-preview-facts">
            <div><dt>Deduction</dt><dd>{step.preview.deduction.name}</dd></div>
            <div><dt>Calculation</dt><dd>{step.preview.deduction.calculationType ? deductionCalculationTypeLabels[step.preview.deduction.calculationType] : 'Not recorded'}</dd></div>
            <div><dt>Effective date</dt><dd>{formatDate(step.preview.effectiveStartDate)}</dd></div>
            <div>
              <dt>Estimate each payroll</dt>
              <dd>
                {step.preview.estimatePerPayroll.amount !== null || step.preview.estimatePerPayroll.amountPerEmployee !== null
                  ? [
                    step.preview.estimatePerPayroll.amountPerEmployee !== null ? `${formatMoney(step.preview.estimatePerPayroll.amountPerEmployee)} for each employee` : null,
                    step.preview.estimatePerPayroll.amount !== null ? `${formatMoney(step.preview.estimatePerPayroll.amount)} in total` : null,
                  ].filter(Boolean).join(', ')
                  : 'Calculated at payroll'}
                <span className="bd-table-note">{step.preview.estimatePerPayroll.basis}</span>
              </dd>
            </div>
            <div>
              <dt>Tax treatment</dt>
              <dd>
                {step.preview.deduction.configurationStatus ? configurationStatusLabels[step.preview.deduction.configurationStatus] : 'Complete (existing deduction)'}
                <span className="bd-table-note">{step.preview.deduction.taxProfileCode ? `Tax profile ${step.preview.deduction.taxProfileCode}` : 'No approved tax profile'}</span>
              </dd>
            </div>
            <div><dt>Status</dt><dd>{step.preview.deduction.isActive ? 'Active' : 'Inactive'}</dd></div>
            <div>
              <dt>Limits</dt>
              <dd>
                {[
                  step.preview.limits.annualLimit !== null ? `${formatMoney(step.preview.limits.annualLimit)} a year` : null,
                  step.preview.limits.perPayPeriodLimit !== null ? `${formatMoney(step.preview.limits.perPayPeriodLimit)} each pay period` : null,
                  step.preview.limits.goalBalance !== null ? `goal ${formatMoney(step.preview.limits.goalBalance)}` : null,
                ].filter(Boolean).join(', ') || 'None set'}
              </dd>
            </div>
          </dl>
          {step.preview.deduction.calculationType !== null && step.preview.deduction.calculationType !== 'FixedAmount' && <p className="bd-help">{NO_CALCULATION_NOTE}</p>}
          {step.preview.conflicts.length > 0 && (
            <div className="bd-notice bd-notice--warn" role="status">
              <p><strong>Conflicts to review</strong></p>
              <ul className="bd-list">{step.preview.conflicts.map((c) => <li key={c}>{c}</li>)}</ul>
            </div>
          )}
          {step.preview.excluded.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 4px' }}>Left out ({step.preview.excluded.length})</h4>
              <ul className="bd-excluded" aria-label="Employees left out">
                {step.preview.excluded.map((x, i) => <li key={`${x.employeeId}-${i}`}>{nameOf(x.employeeId)}: {EXCLUDED_REASON_TEXT[x.reason] ?? x.reason}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {step.kind === 'done' && (
        <div className="bd-form" data-testid="bulk-result">
          <p className="jr-notice jr-notice--ok" role="status">
            <span aria-hidden="true">{'✓'} </span>
            {step.result.replayed
              ? `This assignment was already applied earlier, so nothing was created a second time. ${step.result.createdCount} ${step.result.createdCount === 1 ? 'employee has' : 'employees have'} ${chosen?.name ?? 'the deduction'} from ${formatDate(step.result.effectiveStartDate)}.`
              : `${chosen?.name ?? 'The deduction'} was assigned to ${step.result.createdCount} ${step.result.createdCount === 1 ? 'employee' : 'employees'}, effective ${formatDate(step.result.effectiveStartDate)}.`}
          </p>
          {step.result.excluded.length > 0 && <p className="muted">{step.result.excluded.length} {step.result.excluded.length === 1 ? 'employee was' : 'employees were'} left out.</p>}
          <p className="bd-help">To end an assignment later, use the employee's Benefits / Deductions step.</p>
        </div>
      )}

      {confirming && step.kind === 'preview' && (
        <ConfirmDialog title="Assign this deduction?" confirmLabel="Assign" busy={confirmBusy} error={confirmError} onCancel={() => { if (!confirmBusy) setConfirming(false); }} onConfirm={() => void confirm()}>
          <p className="bd-modal-note">
            {step.preview.deduction.name} will be assigned to {step.preview.affectedCount} {step.preview.affectedCount === 1 ? 'employee' : 'employees'}, effective {formatDate(step.preview.effectiveStartDate)}.
            All of them are assigned together or none are. It does not change any payroll that has already been calculated.
          </p>
        </ConfirmDialog>
      )}
    </ModalDialog>
  );
}
